import type {
  ActivityEntryGridColumn,
  ActivityEntryGridFixedRow,
  ActivityEntryKpiOperand,
  ActivityEntryKpiOption,
  KPI,
} from "../types/activityEntry";

function parseJson<T>(value: unknown): T | null {
  if (value == null) return null;
  if (typeof value !== "string") return value as T;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function resolveKpiOptions(kpi: KPI): ActivityEntryKpiOption[] {
  if (Array.isArray(kpi.options) && kpi.options.length > 0) return kpi.options;
  const snap = parseJson<ActivityEntryKpiOption[]>(kpi.options_snapshot);
  return Array.isArray(snap) ? snap : [];
}

export function resolveKpiOperands(kpi: KPI): {
  operation: string | null;
  operands: ActivityEntryKpiOperand[];
} {
  if (Array.isArray(kpi.operands) && kpi.operands.length > 0) {
    return { operation: kpi.operation ?? null, operands: kpi.operands };
  }
  const snap = parseJson<{
    operation?: string;
    operands?: ActivityEntryKpiOperand[];
  }>(kpi.formula_snapshot);
  if (snap && typeof snap === "object" && !Array.isArray(snap)) {
    return {
      operation: snap.operation ?? kpi.operation ?? null,
      operands: Array.isArray(snap.operands) ? snap.operands : [],
    };
  }
  if (Array.isArray(snap)) {
    return { operation: kpi.operation ?? null, operands: snap };
  }
  return { operation: kpi.operation ?? null, operands: [] };
}

export function resolveKpiGrid(kpi: KPI): {
  columns: ActivityEntryGridColumn[];
  fixedRows: ActivityEntryGridFixedRow[];
  minRows: number | null;
  maxRows: number | null;
  showColumnTotals: boolean;
  showRowTotals: boolean;
} {
  const hasLiveColumns =
    Array.isArray(kpi.grid_columns) && kpi.grid_columns.length > 0;
  const hasLiveFixed =
    Array.isArray(kpi.grid_fixed_rows) && kpi.grid_fixed_rows.length > 0;

  if (hasLiveColumns || hasLiveFixed) {
    return {
      columns: kpi.grid_columns ?? [],
      fixedRows: kpi.grid_fixed_rows ?? [],
      minRows: kpi.grid_min_rows ?? null,
      maxRows: kpi.grid_max_rows ?? null,
      showColumnTotals: !!kpi.grid_show_column_totals,
      showRowTotals: !!kpi.grid_show_row_totals,
    };
  }

  const snap = parseJson<{
    grid_min_rows?: number | null;
    grid_max_rows?: number | null;
    grid_show_column_totals?: boolean | null;
    grid_show_row_totals?: boolean | null;
    columns?: ActivityEntryGridColumn[];
    fixed_rows?: ActivityEntryGridFixedRow[];
  }>(kpi.grid_snapshot);

  if (snap && typeof snap === "object") {
    return {
      columns: Array.isArray(snap.columns) ? snap.columns : [],
      fixedRows: Array.isArray(snap.fixed_rows) ? snap.fixed_rows : [],
      minRows: snap.grid_min_rows ?? kpi.grid_min_rows ?? null,
      maxRows: snap.grid_max_rows ?? kpi.grid_max_rows ?? null,
      showColumnTotals: !!(
        snap.grid_show_column_totals ?? kpi.grid_show_column_totals
      ),
      showRowTotals: !!(snap.grid_show_row_totals ?? kpi.grid_show_row_totals),
    };
  }

  return {
    columns: [],
    fixedRows: [],
    minRows: kpi.grid_min_rows ?? null,
    maxRows: kpi.grid_max_rows ?? null,
    showColumnTotals: !!kpi.grid_show_column_totals,
    showRowTotals: !!kpi.grid_show_row_totals,
  };
}
